import { useEffect, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Flex, Text, Title, Button } from '@nula/core-ui'
import { useAuthStore } from '@/stores/use-auth-store'
import { useUIStore } from '@/stores/use-ui-store'
import { useSessionResume } from '@/lib/auth/use-session-resume'
import { PENDING_REFERRAL_CODE_KEY } from '@/lib/billing/pending-referral'
import styles from './join-by-token.module.css'

export { PENDING_REFERRAL_CODE_KEY }

type RedeemState = 'loading' | 'redirecting' | 'invalid'

interface RedeemReferralProps {
  code: string
}

export function RedeemReferral({ code }: RedeemReferralProps) {
  // This route sits outside MainLayout so AuthInitializer doesn't run for it —
  // bootstrap auth here, mirroring JoinByToken.
  useSessionResume()

  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const isLoading = useAuthStore((s) => s.isLoading)
  const setSettingsOpen = useUIStore((s) => s.setSettingsOpen)
  const [state, setState] = useState<RedeemState>('loading')

  useEffect(() => {
    if (!code) {
      setState('invalid')
      return
    }
    if (isLoading) return

    sessionStorage.setItem(PENDING_REFERRAL_CODE_KEY, code)
    setState('redirecting')

    if (!user) {
      void navigate({ to: '/login', replace: true })
      return
    }

    // Logged in: the code is prefilled at checkout via PENDING_REFERRAL_CODE_KEY.
    // Send them home and open the personal subscription tab, where the promo
    // input lives.
    setSettingsOpen(true, 'subscription')
    void navigate({ to: '/', replace: true })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, isLoading, user])

  if (state === 'invalid') {
    return (
      <Flex align="center" justify="center" className={styles.root}>
        <Flex vertical align="center" gap="md" className={styles.errorBox}>
          <Title level={2}>Referral link is invalid</Title>
          <Text type="secondary">Double-check the URL, or ask your friend to resend it.</Text>
          <Button onClick={() => void navigate({ to: '/' })}>Go to home</Button>
        </Flex>
      </Flex>
    )
  }

  return (
    <Flex align="center" justify="center" className={styles.root}>
      <Text type="secondary">Applying your code…</Text>
    </Flex>
  )
}
